import React, { useState } from 'react';
import { Film } from './Icons';
import { fetchDeviceMedia } from '../services/api';
import './DeviceMedia.css';

const DeviceMedia = ({ deviceId, capturePointId }) => {
  const [count, setCount] = useState(10);
  const [media, setMedia] = useState(null); // null = not queried yet
  const [resolvedCapturePoint, setResolvedCapturePoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleQuery = async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchDeviceMedia(deviceId, count);
      setMedia(result.media || []);
      setResolvedCapturePoint(result.capturePoint_id);
    } catch (err) {
      console.error('Error loading device media:', err);
      setError('Failed to load media: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
  };

  const formatNumber = (value, suffix = '') =>
    value === null || value === undefined ? '—' : `${value}${suffix}`;

  return (
    <div className="device-media">
      <div className="media-controls">
        <div className="media-controls-left">
          <Film size={18} />
          <div className="media-controls-title">
            <h3>Media</h3>
            {(resolvedCapturePoint || capturePointId) && (
              <span className="media-capture-point">
                Capture point: {resolvedCapturePoint || capturePointId}
              </span>
            )}
          </div>
        </div>
        <div className="media-controls-right">
          <label htmlFor="media-count">Recent items</label>
          <input
            id="media-count"
            type="number"
            min="1"
            max="500"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
            disabled={loading}
          />
          <button className="query-media-btn" onClick={handleQuery} disabled={loading}>
            {loading ? 'Loading...' : media === null ? 'Query Media' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="media-error">{error}</div>}

      {media === null && !error && (
        <div className="media-empty">
          <p>Query the most recent media captured by this device.</p>
        </div>
      )}

      {media !== null && media.length === 0 && !loading && (
        <div className="media-empty">
          <p>No media found for this device.</p>
        </div>
      )}

      {media !== null && media.length > 0 && (
        <div className="media-table-container">
          <table className="media-table">
            <thead>
              <tr>
                <th className="col-video">Video</th>
                <th className="col-created">Created At</th>
                <th className="col-concat">Concatenated</th>
                <th className="col-duration">Clip Duration</th>
                <th className="col-offset">Offset [ms]</th>
                <th className="col-user">User ID</th>
                <th className="col-activity">Activity ID</th>
              </tr>
            </thead>
            <tbody>
              {media.map((item) => (
                <tr key={item.id}>
                  <td className="col-video">
                    {item.videoUrl ? (
                      <a
                        href={item.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-link"
                      >
                        View Video →
                      </a>
                    ) : (
                      <span className="no-video">—</span>
                    )}
                  </td>
                  <td className="col-created">{formatTimestamp(item.createdAt)}</td>
                  <td className="col-concat">
                    <span className={`concat-badge ${item.concatenated_video ? 'yes' : 'no'}`}>
                      {item.concatenated_video ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="col-duration">{formatNumber(item.slice_duration_sec, 's')}</td>
                  <td className="col-offset">{formatNumber(item.timestamp_correction_ms)}</td>
                  <td className="col-user">{item.user_id || 'N/A'}</td>
                  <td className="col-activity">{item.activity_id || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeviceMedia;
